import { React } from "react";
import { useHistory } from "react-router-dom";
import HistoryIcon from "@mui/icons-material/History";
import MapsHomeWorkOutlinedIcon from "@mui/icons-material/MapsHomeWorkOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Brightness4OutlinedIcon from "@mui/icons-material/Brightness4Outlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import DeviceHubOutlinedIcon from "@mui/icons-material/DeviceHubOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import NfcIcon from "@mui/icons-material/Nfc";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import ResponsiveCardGrid from "../common/responsive-card-grid";

import { Card, CardActionArea, Typography } from "@mhome/ui";

import {
  useComponentBackgroundColor,
  getBackground,
} from "../common/adaptive-theme-provider";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useRecoilValue } from "recoil";
import { platformState, backgroundTypeState } from "../global-state";

const MenuCard = ({ icon: Icon, title, onClick, getComponentBgColor }) => (
  <Card
    style={{
      backgroundColor: getComponentBgColor("hsl(var(--card))"),
      boxShadow: "none",
      userSelect: "none",
      WebkitTapHighlightColor: "transparent",
    }}
  >
    <CardActionArea
      onClick={onClick}
      style={{
        padding: "16px",
        transition: "transform 0.2s",
      }}
      onFocus={(e) => {
        e.currentTarget.style.outline = "none";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          userSelect: "none",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", userSelect: "none" }}
        >
          <Icon style={{ marginRight: "8px", userSelect: "none" }} />
          <Typography style={{ userSelect: "none" }}>{title}</Typography>
        </div>
        <div style={{ userSelect: "none" }}>
          <Typography style={{ fontWeight: "bold", userSelect: "none" }}>
            &gt;
          </Typography>
        </div>
      </div>
    </CardActionArea>
  </Card>
);

const MenuSection = ({ title, children, getComponentBgColor }) => (
  <div style={{ marginBottom: "12px" }}>
    <Typography
      variant="body1"
      className="text-muted-foreground"
      style={{
        marginBottom: "8px",
        fontWeight: 500,
        fontSize: "0.875rem",
        letterSpacing: "0.1em",
      }}
    >
      {title}
    </Typography>
    <ResponsiveCardGrid spacing={{ xs: 1, sm: 2 }}>
      {children}
    </ResponsiveCardGrid>
  </div>
);

const Menu = () => {
  const history = useHistory();
  const platform = useRecoilValue(platformState);
  const isApp = platform?.source === "app";

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  // Get background type from global state
  const backgroundType = useRecoilValue(backgroundTypeState);

  // Get background style
  const backgroundStyle = getBackground(backgroundType);

  // Menu sections organized by category
  const menuSections = [
    {
      title: "Integration",
      items: [
        {
          icon: ExtensionOutlinedIcon,
          title: "Integrations",
          onClick: () => history.push("/integrations"),
        },
        {
          icon: LightbulbOutlinedIcon,
          title: "Device",
          onClick: () => history.push("/integration?id=basic.device"),
        },
        {
          icon: ChatBubbleOutlineIcon,
          title: "Messaging Apps",
          onClick: () => history.push("/messaging"),
        },
        {
          icon: NfcIcon,
          title: "NFC",
          onClick: () => history.push("/nfc"),
        },
        {
          icon: Brightness4OutlinedIcon,
          title: "Circadian",
          onClick: () => history.push("/circadian"),
        },
      ],
    },
    {
      title: "Automation",
      items: [
        {
          icon: HistoryIcon,
          title: "History",
          onClick: () => history.push("/automation/history"),
        },
        {
          icon: ScheduleIcon,
          title: "Schedule",
          onClick: () => history.push("/schedules"),
        },
        {
          icon: RecordVoiceOverOutlinedIcon,
          title: "Quick Commands",
          onClick: () => history.push("/quickwords"),
        },
        // Conditional automation statistics
        ...(process.env.NODE_ENV !== "production"
          ? [
              {
                icon: BarChartOutlinedIcon,
                title: "Automation Statistics",
                onClick: () => history.push("/automation/statistics"),
              },
            ]
          : []),
      ],
    },
    {
      title: "Hub & Device",
      items: [
        {
          icon: DeviceHubOutlinedIcon,
          title: "Hub Setting",
          onClick: () => history.push("/hub/setting"),
        },
        ...(platform.source === "desktop"
          ? [
              {
                icon: DesktopWindowsOutlinedIcon,
                title: "Desktop Setting",
                onClick: () => history.push("/desktop/setting"),
              },
            ]
          : []),
        ...(isApp
          ? [
              {
                icon: PhoneAndroidIcon,
                title: "Phone Setting",
                onClick: () => history.push("/phone/setting"),
              },
            ]
          : []),
      ],
    },
    {
      title: "Preference",
      items: [
        {
          icon: MapsHomeWorkOutlinedIcon,
          title: "Space",
          onClick: () => history.push("/space"),
        },
        {
          icon: TuneIcon,
          title: "Preference",
          onClick: () => history.push("/preference"),
        },
        {
          icon: PaletteOutlinedIcon,
          title: "Theme",
          onClick: () => history.push("/theme"),
        },
      ],
    },
  ];

  return (
    <div
      style={{
        padding: "16px",
        height: "auto",
        maxWidth: "100%",
        boxSizing: "border-box",
        background: backgroundStyle,
        minHeight: "100vh",
      }}
    >
      {/* Render all menu sections */}
      {menuSections.map((section, sectionIndex) => (
        <MenuSection
          key={sectionIndex}
          title={section.title}
          getComponentBgColor={getComponentBgColor}
        >
          {section.items.map((item, itemIndex) => (
            <MenuCard
              key={itemIndex}
              icon={item.icon}
              title={item.title}
              onClick={item.onClick}
              getComponentBgColor={getComponentBgColor}
            />
          ))}
        </MenuSection>
      ))}
    </div>
  );
};

export default Menu;
